import { CloseOutlined, SaveOutlined } from '@ant-design/icons';
import { Button, Checkbox, Col, Form, Input, Result, Row, Select, Space, Spin } from 'antd';
import dayjs from 'dayjs';
import { useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate, useParams } from 'react-router';
import { StatePieceStatus } from '../../../../../enums';
import { IUpdateBankAccountLookupListFormValues } from '../../../../../interfaces/lookup/bankAccount';
import { IUpdateBankAccount } from '../../../../../interfaces/lookup/bankAccount/data';
import { ContentHeader } from '../../../../../shared/components/appSkeleton/contentHeader/ContentHeader';
import { FetchResult } from '../../../../../shared/components/fetchResult/FetchResult';
import { ROW_GUTTER } from '../../../../../shared/Constants';
import { isError } from '../../../../../shared/helpers';
import { AppDispatch, RootState } from '../../../../../store';
import { fetchBankAccountById, fetchCountriesLookupList, updateBankAccount } from '../../../../../store/lookupSlice/LookupActions';
import { resetState } from '../../../../../store/lookupSlice/LookupSlice';
import { NotFound } from '../../../../notFound/NotFound';

export const BankAccountLookupListEdit = () => {
    const navigate = useNavigate();
    const params = useParams();
    const dispatch = useDispatch<AppDispatch>();
    const [form] = Form.useForm();
    const lookupSlice = useSelector((state: RootState) => state.lookupSlice);
    const { countries } = useSelector((state: RootState) => state.lookupSlice);
    const itemId = params.id ? parseInt(params.id) : undefined;

    const onFinish = (values: IUpdateBankAccountLookupListFormValues) => {
        const body: IUpdateBankAccount = {
            id: lookupSlice.detailsPage.bankAccount.data?.id,
            ...(values as any),
        };

        dispatch(updateBankAccount(body));
    };

    useEffect(() => {
        if (lookupSlice.detailsPage.bankAccount.updateStatus === StatePieceStatus.Success) {
            navigate('/admin/lookup-lists/bank-account', { replace: true });
        }
    }, [lookupSlice.detailsPage.bankAccount.updateStatus]);

    useEffect(() => {
        if (itemId) {
            dispatch(fetchBankAccountById(itemId));
            dispatch(fetchCountriesLookupList());
        }

        return () => {
            dispatch(resetState());
        };
    }, [itemId]);

    useEffect(() => {
        if (lookupSlice.detailsPage.bankAccount.data) {
            form.setFieldsValue({
                id: lookupSlice.detailsPage.bankAccount.data.id,
                name: lookupSlice.detailsPage.bankAccount.data.name,
                bban: lookupSlice.detailsPage.bankAccount.data.bban,
                iban: lookupSlice.detailsPage.bankAccount.data.iban,
                bic: lookupSlice.detailsPage.bankAccount.data.bic,
                sepa: lookupSlice.detailsPage.bankAccount.data.sepa,
                addressline1: lookupSlice.detailsPage.bankAccount.data.addressline1,
                addressline2: lookupSlice.detailsPage.bankAccount.data.addressline2,
                postalCode: lookupSlice.detailsPage.bankAccount.data.postalCode,
                city: lookupSlice.detailsPage.bankAccount.data.city,
                countryId: lookupSlice.detailsPage.bankAccount.data.country?.id,
            } as IUpdateBankAccountLookupListFormValues);
        }
    }, [lookupSlice.detailsPage.bankAccount.data]);

    if (lookupSlice.detailsPage.bankAccount.fetchStatus === StatePieceStatus.Error) {
        return <Result status="warning" title="Data wasn't found or something went wrong." />;
    }

    if (!itemId) {
        return <NotFound />;
    }

    return (
        <Form
            form={form}
            name="bankaccount-lookup-edit"
            onFinish={onFinish}
            layout="vertical"
            onValuesChange={(changedValue: any, values) => {
                form.setFieldsValue(values);
            }}
        >
            <ContentHeader
                title="Manage bank account lookup list"
                breadcrumbs={[
                    {
                        breadcrumbName: 'Admin',
                        path: '/admin',
                    },
                    {
                        breadcrumbName: 'Manage lookup lists',
                        path: '/admin/lookup-lists',
                    },
                    {
                        breadcrumbName: 'Bank account',
                        path: '/admin/lookup-lists/bank-account',
                    },
                    {
                        breadcrumbName: lookupSlice.detailsPage.bankAccount.data?.name || '-',
                        path: `/admin/lookup-lists/bank-account/${params.id}`,
                    },
                    {
                        breadcrumbName: 'Edit',
                        path: `/admin/lookup-lists/bank-account/${params.id}/edit`,
                    },
                ]}
            >
                {!isError(lookupSlice.detailsPage.bankAccount.fetchStatus) && (
                    <Form.Item>
                        <Space>
                            <Button
                                icon={<CloseOutlined />}
                                onClick={() => navigate(-1)}
                                disabled={
                                    lookupSlice.detailsPage.bankAccount.updateStatus === StatePieceStatus.IsFetching ||
                                    lookupSlice.detailsPage.bankAccount.fetchStatus === StatePieceStatus.IsFetching
                                }
                            >
                                Close
                            </Button>
                            <Button
                                htmlType="submit"
                                type="primary"
                                icon={<SaveOutlined />}
                                loading={
                                    lookupSlice.detailsPage.bankAccount.updateStatus === StatePieceStatus.IsFetching ||
                                    lookupSlice.detailsPage.bankAccount.fetchStatus === StatePieceStatus.IsFetching
                                }
                            >
                                Save and close
                            </Button>
                        </Space>
                    </Form.Item>
                )}
            </ContentHeader>

            <div className="page-content">
                <Spin spinning={lookupSlice.detailsPage.bankAccount.fetchStatus === StatePieceStatus.IsFetching}>
                    {isError(lookupSlice.detailsPage.bankAccount.fetchStatus) ? (
                        <FetchResult status={lookupSlice.detailsPage.bankAccount.fetchStatus} about={'Bank account'} />
                    ) : (
                        <>
                            <Row>
                                <Col span={24}>
                                    <Space>
                                        <label className="group-label">Bank account Information</label>
                                    </Space>
                                </Col>
                            </Row>
                            <Row gutter={ROW_GUTTER}>
                                <Col span={8}>
                                    <label className="property required">Name</label>
                                    <Form.Item name="name" rules={[{ required: true, message: "'Name' is required" }]}>
                                        <Input />
                                    </Form.Item>
                                </Col>
                                <Col span={8}>
                                    <label className="property required">BBAN</label>
                                    <Form.Item name="bban">
                                        <Input />
                                    </Form.Item>
                                </Col>
                                <Col span={8}>
                                    <label className="property required">IBAN</label>
                                    <Form.Item name="iban" rules={[{ required: true, message: "'IBAN' is required" }]}>
                                        <Input />
                                    </Form.Item>
                                </Col>
                                <Col span={8}>
                                    <label className="property required">BIC</label>
                                    <Form.Item name="bic" rules={[{ required: true, message: "'BIC' is required" }]}>
                                        <Input />
                                    </Form.Item>
                                </Col>
                                <Col span={8}>
                                    <label className="property required">SEPA</label>
                                    <Form.Item
                                        name="sepa"
                                        rules={[{ required: true, message: "'SEPA' is required" }]}
                                        valuePropName="checked"
                                    >
                                        <Checkbox />
                                    </Form.Item>
                                </Col>
                                <Col span={8}>
                                    <label className="property">Default</label>
                                    <p>{lookupSlice.detailsPage.bankAccount?.data?.isDefault ? 'Yes' : 'No'}</p>
                                </Col>
                            </Row>

                            {/* ADDRESS INFORMATION */}
                            <Row gutter={ROW_GUTTER}>
                                <Col span={24}>
                                    <Space>
                                        <label className="group-label">Address Information</label>
                                    </Space>
                                </Col>
                            </Row>
                            <Row gutter={ROW_GUTTER}>
                                <Col span={8}>
                                    <label className="property required">Address #1</label>
                                    <Form.Item name="addressline1" rules={[{ required: true, message: "'Address' is required" }]}>
                                        <Input />
                                    </Form.Item>
                                </Col>
                                <Col span={8}>
                                    <label className="property">Address #2</label>
                                    <Form.Item name="addressline2">
                                        <Input />
                                    </Form.Item>
                                </Col>
                                <Col span={8}>
                                    <label className="property required">Postal code</label>
                                    <Form.Item name="postalCode" rules={[{ required: true, message: "'Postal code' is required" }]}>
                                        <Input />
                                    </Form.Item>
                                </Col>
                                <Col span={8}>
                                    <label className="property required">City</label>
                                    <Form.Item name="city" rules={[{ required: true, message: "'City' is required" }]}>
                                        <Input />
                                    </Form.Item>
                                </Col>
                                <Col span={8}>
                                    <label className="property required">Country</label>
                                    <Form.Item name="countryId" rules={[{ required: true, message: "'Country' is required" }]}>
                                        <Select
                                            showSearch
                                            placeholder="Select a country"
                                            filterOption={(input, option) =>
                                                (option!.children as unknown as string).toLowerCase().startsWith(input.toLowerCase())
                                            }
                                        >
                                            {countries.lookup.data.map((country) => (
                                                <Select.Option key={country.key} value={country.key} disabled={country.isDeleted}>
                                                    {country.description}
                                                </Select.Option>
                                            ))}
                                        </Select>
                                    </Form.Item>
                                </Col>
                            </Row>

                            {/* INTERNAL INFORMATION */}
                            <Row className="margin-top">
                                <Col span={24}>
                                    <label className="group-label">Internal Information</label>
                                </Col>
                            </Row>
                            <Row gutter={ROW_GUTTER}>
                                <Col span={8}>
                                    <label className="property">Created by</label>
                                    <p>{lookupSlice.detailsPage.bankAccount?.data?.createdBy?.name || '-'}</p>
                                </Col>
                                <Col span={8}>
                                    <label className="property">Created on</label>
                                    <p className="capitalize">
                                        {lookupSlice.detailsPage.bankAccount.data?.created
                                            ? dayjs(lookupSlice.detailsPage.bankAccount.data?.created).format('MMMM D, YYYY HH:mm')
                                            : '-'}
                                    </p>
                                </Col>
                                <Col span={8}>&nbsp;</Col>
                            </Row>
                            <Row gutter={ROW_GUTTER}>
                                <Col span={8}>
                                    <label className="property">Last modified by</label>
                                    <p>{lookupSlice.detailsPage.bankAccount.data?.modifiedBy?.name || '-'}</p>
                                </Col>
                                <Col span={8}>
                                    <label className="property">Last modified on</label>
                                    <p className="capitalize">
                                        {lookupSlice.detailsPage.bankAccount.data?.modified
                                            ? dayjs(lookupSlice.detailsPage.bankAccount.data?.modified).format('MMMM D, YYYY HH:mm')
                                            : '-'}
                                    </p>
                                </Col>
                                <Col span={8}>&nbsp;</Col>
                            </Row>
                        </>
                    )}
                </Spin>
            </div>
        </Form>
    );
};
